import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GitBranch, Loader2, Radio, ScanSearch, User } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../../lib/ctoMembers';
import {
  fetchAllReviews,
  flattenFindings,
  reviewSubject,
  subscribeCtoReviewsLive,
  updateFindingStatus,
  type CtoReviewRow,
  type FindingStatus,
} from '../../lib/ctoReviews';
import { CtoFindingBoard, CtoFindingViewToggle } from './CtoFindingBoard';
import { CtoFindingMatrix } from './CtoFindingMatrix';
import { CtoFindingRow } from './CtoFindingRow';

/**
 * Everything the review stack has collected, across every member and repository.
 *
 * Runs on the left, what they found on the right. The member page answers "what should this
 * client do"; this one answers "what is coming back at all" — which is the question you have
 * before you know whose page to open.
 */

const SELECT_CLASS =
  'px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-700 dark:text-gray-200';

export default function CtoReviewsView() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<CtoReviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [view, setView] = useState<'list' | 'narrative'>('list');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setReviews(await fetchAllReviews());
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load reviews');
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useCallback(async () => {
    try {
      setReviews(await fetchAllReviews());
    } catch {
      /* Live refresh — do not toast on every tick. */
    }
  }, []);

  useEffect(() => subscribeCtoReviewsLive(() => void refresh()), [refresh]);

  const visible = useMemo(
    () =>
      reviews.filter(
        (r) =>
          (sourceFilter === 'all' || r.source === sourceFilter) &&
          (statusFilter === 'all' || r.status === statusFilter),
      ),
    [reviews, sourceFilter, statusFilter],
  );

  const selected = useMemo(
    () => visible.find((r) => r.id === selectedId) ?? null,
    [visible, selectedId],
  );

  // Nothing selected means "show me everything that came back", which is the more useful
  // landing state than an empty panel telling you to pick something.
  const findings = useMemo(
    () => flattenFindings(selected ? [selected] : visible),
    [selected, visible],
  );

  const stats = useMemo(() => {
    const all = flattenFindings(reviews);
    return {
      runs: reviews.length,
      repos: new Set(reviews.map((r) => r.repo)).size,
      people: new Set(reviews.map((r) => r.subscriberId ?? r.clientLabel)).size,
      open: all.filter((f) => f.status === 'open').length,
    };
  }, [reviews]);

  const setStatus = async (findingId: string, status: FindingStatus) => {
    try {
      await updateFindingStatus(findingId, status);
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not update the finding');
    }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <ScanSearch className="h-7 w-7 text-fuchsia-500" />
          Reviews
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide border bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800">
            <Radio className="h-3 w-3 animate-pulse" />
            Live
          </span>
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-3xl">
          Everything the review stack has collected. {stats.runs} runs across {stats.repos}{' '}
          repositories and {stats.people} people, with {stats.open} findings still open.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <select
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
          className={SELECT_CLASS}
        >
          <option value="all">Any source</option>
          <option value="manual">Manual session</option>
          <option value="mcp">From the connector</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={SELECT_CLASS}
        >
          <option value="all">Any run status</option>
          <option value="running">Running</option>
          <option value="complete">Complete</option>
          <option value="abandoned">Abandoned</option>
        </select>
        {selected && (
          <button
            type="button"
            onClick={() => setSelectedId(null)}
            className="text-sm text-fuchsia-600 dark:text-fuchsia-400 hover:underline"
          >
            Show all findings
          </button>
        )}
        <CtoFindingViewToggle view={view} onChange={setView} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400 py-24">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading…
        </div>
      ) : reviews.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-16">
          Nothing collected yet. Start a review from a member's page.
        </p>
      ) : (
        <>
        <CtoFindingMatrix findings={findings} />
        {view === 'narrative' ? (
          <CtoFindingBoard
            findings={findings}
            onStatus={(id, status) => void setStatus(id, status)}
          />
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
          <aside className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700 overflow-hidden self-start">
            {visible.map((r) => {
              const open = r.findings.filter((f) => f.status === 'open').length;
              return (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setSelectedId(r.id === selectedId ? null : r.id)}
                  className={`w-full text-left px-4 py-3 transition-colors ${
                    r.id === selectedId
                      ? 'bg-fuchsia-50 dark:bg-fuchsia-900/20'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                  }`}
                >
                  <div className="flex items-center gap-1.5 text-sm font-medium text-gray-900 dark:text-white">
                    <User className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <span className="truncate">{reviewSubject(r)}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 mt-1">
                    <GitBranch className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                    <span className="truncate font-mono">{r.repo}</span>
                  </div>
                  <div className="text-[11px] text-gray-400 mt-1">
                    {formatDate(r.reviewedAt)} · {r.source} · {r.status} · {open} open of{' '}
                    {r.findings.length}
                  </div>
                </button>
              );
            })}
          </aside>

          <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                {selected ? `${reviewSubject(selected)} — ${selected.repo}` : 'All findings'}
                <span className="ml-2 font-normal text-gray-400">{findings.length}</span>
              </h2>
              {selected?.subscriberId && (
                <button
                  type="button"
                  onClick={() => navigate(`/cto/members/${selected.subscriberId}?tab=reviews`)}
                  className="text-xs text-fuchsia-600 dark:text-fuchsia-400 hover:underline shrink-0"
                >
                  Open member
                </button>
              )}
            </div>
            {findings.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 p-10 text-center">
                No findings recorded against{' '}
                {selected ? 'this run' : 'anything matching those filters'} yet.
              </p>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {findings.map((f) => (
                  <CtoFindingRow
                    key={f.id}
                    finding={f}
                    showSubject={!selected}
                    onStatus={(status) => void setStatus(f.id, status)}
                  />
                ))}
              </ul>
            )}
          </section>
        </div>
        )}
        </>
      )}
    </div>
  );
}
